import { reviver } from '@airreading/common';
import { Temporal } from 'temporal-polyfill';
import { ZodSchema } from 'zod';

interface StoredData<T> {
  formData: T;
  lastUpdated: string;
}

export function shortTermStorage<T>(formKey: string, zodSchema?: ZodSchema<T>) {
  return {
    set: (value: T) => {
      const parsedData = zodSchema ? zodSchema.parse(value) : value;
      // Use Temporal.Instant to ensure UTC timezone consistency with database timestamps
      // This matches how updatedAt and draftSavedAt are stored in SessionFeedback table
      const storedData: StoredData<T> = {
        formData: parsedData,
        lastUpdated: Temporal.Now.instant().toString(),
      };
      localStorage.setItem(formKey, JSON.stringify(storedData));
    },
    get: () => {
      const savedData = localStorage.getItem(formKey);
      if (savedData) {
        try {
          const data = JSON.parse(savedData, reviver);

          // Handle legacy format (data without lastUpdated field)
          // Check if data has the new structure with formData and lastUpdated
          if (data && typeof data === 'object' && 'formData' in data && 'lastUpdated' in data) {
            const parsedData = zodSchema ? zodSchema.parse(data.formData) : data.formData;
            return parsedData;
          }

          // Legacy format: data is the form data directly
          const parsedData = zodSchema ? zodSchema.parse(data) : data;
          return parsedData;
        } catch (error) {
          console.error('Failed to parse saved form data:', error);
          // Remove invalid data from localStorage to prevent future errors
          localStorage.removeItem(formKey);
          // Return empty object instead of throwing to allow form to initialize with default values
          return {} as T;
        }
      }
      return {} as T;
    },
    getLastUpdated: (): string | null => {
      const savedData = localStorage.getItem(formKey);
      if (savedData) {
        try {
          const data = JSON.parse(savedData, reviver) as StoredData<T>;
          return data.lastUpdated || null;
        } catch (error) {
          return null;
        }
      }
      return null;
    },
    remove: () => {
      localStorage.removeItem(formKey);
    },
  };
}
